import React, { useState, useEffect } from "react";
import { Person } from "@mui/icons-material";
import Swal from "sweetalert2";

const UnassignReassignModal = ({ isOpen, onClose, selectedLoans, selectAllMode = false, totalCount = 0, filters = {}, onSuccess }) => {
  const [officers, setOfficers] = useState([]);
  const [selectedOfficer, setSelectedOfficer] = useState("");
  const [action, setAction] = useState("unassign"); // "unassign" or "reassign"
  const [loading, setLoading] = useState(false);
  const [loadingOfficers, setLoadingOfficers] = useState(false);

  // Fetch officers
  const fetchOfficers = async () => {
    setLoadingOfficers(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST_COLLECTIONS}/collections-officers`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      const officersList = data.collectionsOfficers || [];
      // Sort officers by first name alphabetically
      const sortedOfficers = officersList.sort((a, b) => {
        const firstNameA = (a.firstName || "").toLowerCase();
        const firstNameB = (b.firstName || "").toLowerCase();
        return firstNameA.localeCompare(firstNameB);
      });
      setOfficers(sortedOfficers);
    } catch (error) {
      console.error("Error fetching officers:", error);
    } finally {
      setLoadingOfficers(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchOfficers();
    }
  }, [isOpen]);

  const handleUnassign = async () => {
    if (selectedLoans.length === 0 && !selectAllMode) {
      Swal.fire({
        icon: "warning",
        title: "No Loans Selected",
        text: "Please select at least one loan to unassign.",
      });
      return;
    }

    setLoading(true);
    try {
      const requestBody = selectAllMode
        ? {
            filters: {
              bucket: filters.bucket || null,
              minAmount: filters.minAmount || null,
              maxAmount: filters.maxAmount || null,
              search: filters.search || null,
              officerId: filters.officerId || null
            }
          }
        : {
            loanIds: selectedLoans,
          };

      const response = await fetch(
        `${process.env.REACT_APP_API_HOST_COLLECTIONS}/unassign-loans`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );
      const result = await response.json();
      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: result.message || `${selectAllMode ? totalCount : selectedLoans.length} loan(s) unassigned successfully.`,
        });
        onSuccess();
        onClose();
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: result.message || "Failed to unassign loans.",
        });
      }
    } catch (error) {
      console.error("Error unassigning loans:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error unassigning loans. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReassign = async () => {
    if (selectedLoans.length === 0 && !selectAllMode) {
      Swal.fire({
        icon: "warning",
        title: "No Loans Selected",
        text: "Please select at least one loan to reassign.",
      });
      return;
    }
    if (!selectedOfficer) {
      Swal.fire({
        icon: "warning",
        title: "No Officer Selected",
        text: "Please select an officer to reassign loans to.",
      });
      return;
    }

    setLoading(true);
    try {
      const requestBody = selectAllMode
        ? {
            officerId: selectedOfficer,
            filters: {
              bucket: filters.bucket || null,
              minAmount: filters.minAmount || null,
              maxAmount: filters.maxAmount || null,
              search: filters.search || null,
              officerId: filters.officerId || null
            }
          }
        : {
            officerId: selectedOfficer,
            loanIds: selectedLoans,
          };

      const response = await fetch(
        `${process.env.REACT_APP_API_HOST_COLLECTIONS}/assign-loans`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );
      const result = await response.json();
      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: result.message || `${selectAllMode ? totalCount : selectedLoans.length} loan(s) reassigned successfully.`,
        });
        onSuccess();
        onClose();
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: result.message || "Failed to reassign loans.",
        });
      }
    } catch (error) {
      console.error("Error reassigning loans:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error reassigning loans. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-[90%] max-w-2xl h-auto max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="border-b p-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">Unassign / Reassign Loans</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            ✕
          </button>
        </div>

        {/* Modal Content */}
        <div className="flex-grow overflow-y-auto p-6">
          <div className="mb-6">
            <p className="text-gray-700 mb-4">
              {selectedLoans.length} loan(s) selected
            </p>

            {/* Action Selection */}
            <div className="mb-6">
              <label className="block font-bold mb-3 text-gray-700">Select Action</label>
              <div className="flex gap-4">
                <button
                  onClick={() => setAction("unassign")}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                    action === "unassign"
                      ? "bg-red-500 text-white hover:bg-red-600"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  Unassign
                </button>
                <button
                  onClick={() => setAction("reassign")}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                    action === "reassign"
                      ? "bg-blue-500 text-white hover:bg-blue-600"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  Reassign
                </button>
              </div>
            </div>

            {/* Officer Selection (only for reassign) */}
            {action === "reassign" && (
              <div className="mb-6">
                <label className="block font-bold mb-3 text-gray-700">Assign To</label>
                {loadingOfficers ? (
                  <div className="text-gray-500 text-sm">Loading Officers...</div>
                ) : officers.length === 0 ? (
                  <div className="text-red-500 text-sm">No officers found. Please ensure agents are assigned to you in the admin portal.</div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {officers.map((officer) => {
                      const displayName = officer.firstName;
                      const isSelected = selectedOfficer === officer._id;

                      return (
                        <button
                          key={officer._id}
                          onClick={() => setSelectedOfficer(isSelected ? "" : officer._id)}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                            isSelected
                              ? "bg-blue-500 text-white hover:bg-blue-600"
                              : "bg-blue-100 text-gray-700 hover:bg-blue-200"
                          }`}
                        >
                          {displayName}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Action Description */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              {action === "unassign" ? (
                <p className="text-gray-700">
                  This will remove the assignment from the selected loan(s). They will become unassigned and can be assigned to another officer later.
                </p>
              ) : (
                <p className="text-gray-700">
                  This will reassign the selected loan(s) to the chosen officer. The previous assignment will be replaced.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="border-t p-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={action === "unassign" ? handleUnassign : handleReassign}
            className={`px-6 py-2 rounded font-medium text-white ${
              action === "unassign"
                ? "bg-red-500 hover:bg-red-600"
                : "bg-blue-500 hover:bg-blue-600"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
            disabled={loading || (action === "reassign" && !selectedOfficer)}
          >
            {loading ? "Processing..." : action === "unassign" ? "Unassign Loans" : "Reassign Loans"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnassignReassignModal;

